import gql from "graphql-tag";
import {UCUMUnit} from "../../../helper/UCUMUnit";

export const getPrescriptionRules = gql`
  {
    expertToolPrescriptionRules(
      orders: { field: calculationSequence, direction: asc }
    ) {
      key
      name
      type
      intensityLabel
      frequencyLabel
      sessionDurationLabel
      programDurationLabel
      strengthTrainingLabel
      safetyPrecautions {
        text
      }
    }
  }`;

export const getPreviousPrescription = gql`query getPreviousPrescription(
    $patientId: String!
  ) {
    patientPrescriptions(
      functions: [{ function: patientId, parameters: [$patientId] }],
      orders: [{ field: createdAt, direction: desc }],
      limit: 1,
    ) {
      id
      heartRate
      heartRateCooldown
      intensityLabel
      frequencyLabel
      sessionDurationLabel
      programDurationLabel
      strengthTrainingLabel
      additionalTrainingStrategies
      strengthTrainingDetails
      extraInfoIntensities
      extraInfoFrequencies
      extraInfoSessions
      intensities {
        min
        max
        flags
      }
      selectedRules{
        name
        key
        safetyPrecautions{
          text
        }
      }
    }
  }`;

export const getDefaultRules = gql`query getDefaultRules($patientId: Int!)
  {
    defaultPatientPrescriptionRules(patientId: $patientId) {
      key
    }
  }`;

export const runExpertToolAlgorithm = gql`mutation runExpertToolAlgorithm(
    $patientId: Int!,
    $selectedRules: [String!]!,
    $hrICDTreshold: Int,
    $hrVentricularTachycardia: Int,
    $hrMyocardialIschemicTreshold: Int,
    $alwaysRun: Boolean
  ) {
    runExpertToolAlgorithm (
      runAlgorithm: {
        patientId: $patientId,
        selectedRules: $selectedRules,
        hrICDTreshold: $hrICDTreshold,
        hrVentricularTachycardia: $hrVentricularTachycardia,
        hrMyocardialIschemicTreshold: $hrMyocardialIschemicTreshold,
        alwaysRun: $alwaysRun
      }
    ) {
      id
      heartRate
      intensityLabel
      intensities {
        min
        max
        flags
      }
      minFrequency
      maxFrequency
      frequencyFlags
      frequencyLabel
      minSessionDuration
      maxSessionDuration
      sessionDurationLabel
      sessionDurationFlags
      minProgramDuration
      maxProgramDuration
      programDurationFlags
      programDurationLabel
      strengthTrainingFlags
      strengthTrainingLabel
      additionalTrainingStrategies
      strengthTrainingDetails
      extraInfoIntensities
      extraInfoFrequencies
      extraInfoSessions
      algorithmRan
      changed
      changeReason
      hasHIT
      selectedRules{
        name
        safetyPrecautions{
          text
        }
      }
    }
  }`;

export const createUpdatePrescription = gql`mutation createUpdatePrescription(
    $patientId: Int!,
    $selectedRules: [String!]!,
    $hrMyocardialIschemicTreshold: Int,
    $hrVentricularTachycardia: Int,
    $hrICDTreshold: Int,
    $new: Boolean!,
    $startNextWeek: Boolean,
    $minIntensity: String,
    $maxIntensity: String,
    $intensityFlags: [String!],
    $intensityIncludesHIT: Boolean,
    $minFrequency: Int,
    $maxFrequency: Int,
    $frequencyFlags: [String!],
    $minSessionDuration: Int,
    $maxSessionDuration: Int,
    $sessionDurationFlags: [String!],
    $minProgramDuration: Int,
    $maxProgramDuration: Int,
    $programDurationFlags: [String!],
    $strengthTrainingFlags: [String!],
    $additionalTraining: String,
    $changeReason: String,
    $heartRate: Int
    $heartRateCooldown: Int
  ) {
    createUpdateExpertToolPrescription(
      runAlgorithm: {
        patientId: $patientId,
        selectedRules: $selectedRules,
        hrICDTreshold: $hrICDTreshold,
        hrMyocardialIschemicTreshold: $hrMyocardialIschemicTreshold,
        hrVentricularTachycardia: $hrVentricularTachycardia,
      }
      new: $new
      startNextWeek: $startNextWeek
      minIntensity: $minIntensity
      maxIntensity: $maxIntensity
      intensityFlags: $intensityFlags
      intensityIncludesHIT: $intensityIncludesHIT
      minFrequency: $minFrequency
      maxFrequency: $maxFrequency
      frequencyFlags: $frequencyFlags
      minSessionDuration: $minSessionDuration
      maxSessionDuration: $maxSessionDuration
      sessionDurationFlags: $sessionDurationFlags
      minProgramDuration: $minProgramDuration
      maxProgramDuration: $maxProgramDuration
      programDurationFlags: $programDurationFlags
      strengthTrainingFlags: $strengthTrainingFlags
      additionalTraining: $additionalTraining
      changeReason: $changeReason
      heartRate: $heartRate
      heartRateCooldown: $heartRateCooldown
    ) {
      id
      heartRate
      heartRateCooldown
      intensityLabel
      intensities {
        min
        max
        flags
      }
      minFrequency
      maxFrequency
      frequencyFlags
      frequencyLabel
      minSessionDuration
      maxSessionDuration
      sessionDurationLabel
      sessionDurationFlags
      minProgramDuration
      maxProgramDuration
      programDurationFlags
      programDurationLabel
      strengthTrainingFlags
      strengthTrainingLabel
      additionalTrainingStrategies
      strengthTrainingDetails
      extraInfoIntensities
      extraInfoFrequencies
      extraInfoSessions
      changeReason
      changed
      hasHIT
      selectedRules{
        name
        safetyPrecautions{
          text
        }
      }
    }
  }`;

export const getPatientPrescriptions = gql`query getPatientPrescriptions(
    $patientId: String!,
  ) {
    patientPrescriptions(
      functions: [{ function: patientId, parameters: [$patientId] }],
      orders: [{ field: createdAt, direction: desc }],
    ) {
      id
      new
      createdAt
      endDate
      heartRate
      heartRateCooldown
      intensityLabel
      frequencyLabel
      sessionDurationLabel
      programDurationLabel
      strengthTrainingLabel
      selectedRules{
        name
        type
      }
    }
  }`;

export const getPatientSummaryAtDate = gql`query getPatientSummaryAtDate(
    $patientId: Int!,
    $atDate: DateTime,
  ) {
    patientSummary(patientId: $patientId, atDate: $atDate) {
      user {
        knowledgeLevel {
          key
          name
          level
        }
      }
      bloodPressure {
        component {
          code {
            coding {
              code
            }
          }
          valueQuantity(conversion: [{ toUnit: "${ UCUMUnit.MILLIMETER_OF_MERCURY.unit }" }]) {
            id
            value
          }
        }
        status
      }
      weight {
        effectiveDateTime
        valueQuantity(conversion: [{ toUnit: "${ UCUMUnit.KILOGRAM.unit }" }]) {
          value
        }
      }
      length {
        effectiveDateTime
        valueQuantity(conversion: [{ toUnit: "${ UCUMUnit.CENTIMETER.unit }" }]) {
          value
        }
      }
      BMI {
        effectiveDateTime
        valueQuantity {
          code
          value
        }
      }
      LDL {
        valueQuantity(conversion: [{ toUnit: "${ UCUMUnit.MILLIGRAM_PER_DECILITER.unit }" }]) {
          value
        }
      }
      HDL {
        valueQuantity(conversion: [{toUnit: "${ UCUMUnit.MILLIGRAM_PER_DECILITER.unit }"}]) {
          value
        }
      }
      cholesterol {
        valueQuantity(conversion: [{toUnit: "${ UCUMUnit.MILLIGRAM_PER_DECILITER.unit }"}]) {
          value
        }
      }
      HbA1c {
        valueQuantity(conversion: [{ toUnit: "${ UCUMUnit.PERCENT.unit }" }]) {
          value
        }
      }
      outcomeGoals {
        key
        value
        label
        color
        date
        extraInformation
      }
      behaviourGoals {
        key
        value
        label
        color
        date
        extraInformation
      }
      heartRate {
        valueQuantity(conversion: [{ toUnit: "${ UCUMUnit.PERCENT.unit }" }]) {
          value
        }
      }
      lastCompletedSixMinuteWalkingTest {
        distanceWalked
      }
    }
  }`;
